// What this tenant's Copilot accepts, read from the BFF before the composer
// offers a button (SPEC-COPILOT-02 §6, governance rule 8).
//
// The runtime answers for the tenant's resolved provider, so the camera, the
// paperclip, the microphone and the speaker are rendered only when the turn
// they would produce can actually be read. A capability the runtime does not
// advertise removes the control; it never renders a button that ends in a 422.
//
// Lookup failures remain visible and never establish text-only availability.

import { useQuery } from "@tanstack/react-query";
import { httpRequest } from "@/lib";
import type { MediaModality } from "@/lib/media-prep";
import { APP_CONSTANTS } from "@/config";

type CapabilitiesResponse = {
  input_modalities?: unknown;
  stt?: unknown;
  tts?: unknown;
  max_file_bytes?: unknown;
  max_turn_bytes?: unknown;
};

export type CopilotCapabilities = {
  inputModalities: MediaModality[];
  stt: boolean;
  tts: boolean;
  maxFileBytes: number | null;
  maxTurnBytes: number | null;
};

/** Text only: every optional control is hidden. */
export const TEXT_ONLY_CAPABILITIES: CopilotCapabilities = {
  inputModalities: [],
  stt: false,
  tts: false,
  maxFileBytes: null,
  maxTurnBytes: null,
};

const KNOWN_MODALITIES: readonly MediaModality[] = ["image", "pdf", "audio", "text"];

function parseCapabilities(body: CapabilitiesResponse): CopilotCapabilities {
  const modalities = Array.isArray(body.input_modalities) ? body.input_modalities : [];

  return {
    inputModalities: KNOWN_MODALITIES.filter((modality) => modalities.includes(modality)),
    stt: body.stt === true,
    tts: body.tts === true,
    maxFileBytes: typeof body.max_file_bytes === "number" ? body.max_file_bytes : null,
    maxTurnBytes: typeof body.max_turn_bytes === "number" ? body.max_turn_bytes : null,
  };
}

async function fetchCapabilities(): Promise<CopilotCapabilities> {
  const { data } = await httpRequest.get<CapabilitiesResponse>("/copilot/capabilities");
  if (!data || !Array.isArray(data.input_modalities)) throw new Error("Invalid capability response");
  return parseCapabilities(data);
}

export function useCopilotCapabilities(): {
  capabilities: CopilotCapabilities;
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  retry: () => void;
} {
  const tenantId = localStorage.getItem(APP_CONSTANTS.COMPANY_LOCAL_STORAGE_KEY);
  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ["copilot-capabilities", tenantId],
    queryFn: fetchCapabilities,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  return { capabilities: isError ? TEXT_ONLY_CAPABILITIES : data ?? TEXT_ONLY_CAPABILITIES, isLoading, isError, isFetching, retry: () => { void refetch(); } };
}

export function supportsModality(
  capabilities: CopilotCapabilities,
  modality: MediaModality,
): boolean {
  return capabilities.inputModalities.includes(modality);
}
